/**
 * Locale-aware formatting. Nothing in the UI hardcodes a date order, a clock
 * style, a first day of the week or a currency symbol — it all comes from
 * `Intl` for the active locale, so DE reads 18.09.2026, NL/FR/ES/IT read
 * 18/09/2026, and the week starts on Monday where the locale says so.
 */

/** Monday. ISO 8601 and every European locale; also our fallback. */
const MONDAY = 1;

type WeekInfo = { firstDay?: number };
type LocaleWithWeekInfo = Intl.Locale & { getWeekInfo?: () => WeekInfo; weekInfo?: WeekInfo };

/** Where the brand works (ISO 3166-1 alpha-2), set from its profile: decides date order for a language without a region. */
let workspaceRegion: string | null = null;
const REGION_KEY = 'unipostudio.formatRegion';
try {
  const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(REGION_KEY) : null;
  if (saved && /^[A-Z]{2}$/.test(saved)) workspaceRegion = saved;
} catch {
  /* storage unavailable: the region arrives with the brand profile */
}

/**
 * Sets the brand's country as the region dates and numbers are written for
 * when the UI language carries none: an Indian business on an en-US browser
 * reads 2/11/2026 for 2 November, not 11/02/2026. Null clears it.
 */
export function setFormattingRegion(country: string | null | undefined): void {
  const next = country && /^[A-Za-z]{2}$/.test(country) ? country.toUpperCase() : null;
  workspaceRegion = next;
  try {
    if (next) localStorage.setItem(REGION_KEY, next);
    else localStorage.removeItem(REGION_KEY);
  } catch {
    /* storage unavailable: kept for this session only */
  }
}

/** `<lang>-<REGION>` when the engine knows it, else null. */
function withRegion(lang: string, region: string): string | null {
  const tag = `${lang}-${region}`;
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([tag]).length > 0 ? tag : null;
  } catch {
    return null;
  }
}

/**
 * The locale to format with. The UI language comes from <html lang> (set by
 * the language switcher).
 *
 * - A UI language chosen with its region (pt-BR) is kept.
 * - Otherwise the brand's country gives the region (en + IN → en-IN), so a
 *   business in India reads Indian dates whatever the browser says.
 * - Without one, the browser's own locale when it is the same language
 *   (it carries the region: en-GB vs en-US).
 */
export function getActiveLocale(): string {
  const ui = typeof document !== 'undefined' ? document.documentElement.lang : '';
  const browser = typeof navigator !== 'undefined' ? navigator.language : '';
  const uiLang = ui.split('-')[0];
  if (ui.includes('-')) return ui;
  if (uiLang && workspaceRegion) {
    const regional = withRegion(uiLang, workspaceRegion);
    if (regional) return regional;
  }
  if (uiLang && browser && browser.split('-')[0] === uiLang) return browser;
  return ui || browser || 'en-GB';
}

/**
 * First day of the week as a JS weekday index (0 = Sunday … 6 = Saturday).
 * Uses Intl.Locale week info where the engine has it, Monday otherwise.
 */
export function getWeekStart(locale: string = getActiveLocale()): number {
  try {
    const loc = new Intl.Locale(locale) as LocaleWithWeekInfo;
    const info = typeof loc.getWeekInfo === 'function' ? loc.getWeekInfo() : loc.weekInfo;
    const first = info?.firstDay; // 1 = Monday … 7 = Sunday
    if (typeof first === 'number' && first >= 1 && first <= 7) return first % 7;
  } catch {
    // Invalid locale tag — fall through to Monday.
  }
  return MONDAY;
}

/** Weekday names in display order, starting from the locale's first day. */
export function getWeekdayNames(
  format: 'long' | 'short' | 'narrow' = 'short',
  locale: string = getActiveLocale(),
): string[] {
  const start = getWeekStart(locale);
  const fmt = new Intl.DateTimeFormat(locale, { weekday: format });
  // 2024-01-07 is a Sunday; offset from it to get each weekday.
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 7 + ((start + i) % 7))));
}

function toDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value);
}

/** 18/09/2026 in NL, 18.09.2026 in DE, 9/18/2026 in en-US. */
export function formatDate(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' },
  locale: string = getActiveLocale(),
): string {
  return new Intl.DateTimeFormat(locale, options).format(toDate(value));
}

/** "Fri 18 Sep" style, in the locale's own order and words. */
export function formatDayLabel(value: Date | string | number, locale: string = getActiveLocale()): string {
  return formatDate(value, { weekday: 'short', day: 'numeric', month: 'short' }, locale);
}

/** 14:30 where the locale uses a 24-hour clock, 2:30 PM where it does not. */
export function formatTime(value: Date | string | number, locale: string = getActiveLocale()): string {
  return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(toDate(value));
}

export function formatDateTime(value: Date | string | number, locale: string = getActiveLocale()): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(toDate(value));
}

/** Whether the locale writes times on a 24-hour clock. */
export function uses24HourClock(locale: string = getActiveLocale()): boolean {
  const parts = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).formatToParts(new Date(2024, 0, 1, 13));
  return !parts.some((p) => p.type === 'dayPeriod');
}

/** An hour on the locale's clock: "19:00" or "7 PM". */
export function formatHour(hour: number, locale: string = getActiveLocale()): string {
  const date = new Date(2024, 0, 1, hour);
  return uses24HourClock(locale)
    ? new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date)
    : new Intl.DateTimeFormat(locale, { hour: 'numeric' }).format(date);
}

export type WorkspaceCurrency = 'EUR' | 'GBP' | 'INR' | 'USD';

/** Display only. Plan pricing and Razorpay amounts are never computed here. */
export function formatMoney(
  amount: number,
  currency: WorkspaceCurrency | string,
  options: { locale?: string; maximumFractionDigits?: number } = {},
): string {
  const { locale: active = getActiveLocale(), maximumFractionDigits } = options;
  const locale = currency === 'INR' ? inrLocale(active) : active;
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    ...(maximumFractionDigits !== undefined ? { maximumFractionDigits, minimumFractionDigits: 0 } : {}),
  }).format(amount);
}

/** Languages whose rupee amounts are grouped in lakhs (₹1,00,000). */
const LAKH_LANGUAGES: ReadonlySet<string> = new Set(['en', 'hi', 'bn', 'ml', 'ta', 'te', 'kn', 'ur']);

/**
 * Rupees are written the Indian way whatever region the browser names
 * (AL-29): an en-US reader sees ₹1,00,000, not ₹100,000. Other languages
 * keep their own format.
 */
function inrLocale(locale: string): string {
  const base = (locale.split('-')[0] ?? '').toLowerCase();
  return LAKH_LANGUAGES.has(base) ? `${base}-IN` : locale;
}

export function formatNumber(value: number, locale: string = getActiveLocale()): string {
  return new Intl.NumberFormat(locale).format(value);
}

export function formatCompact(value: number, locale: string = getActiveLocale()): string {
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

export function formatPercent(fraction: number, locale: string = getActiveLocale()): string {
  return new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(fraction);
}

/**
 * A conversion rate: whole percents, but a rate under 1% keeps one
 * significant digit ("0.3%"), so 38 conversations from 12,400 reached never
 * reads as 0%.
 */
export function formatRate(fraction: number, locale: string = getActiveLocale()): string {
  if (fraction > 0 && fraction < 0.01) return new Intl.NumberFormat(locale, { style: 'percent', maximumSignificantDigits: 1 }).format(fraction);
  return formatPercent(fraction, locale);
}

const RELATIVE_STEPS: { limit: number; divisor: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { limit: 60, divisor: 1, unit: 'second' },
  { limit: 3600, divisor: 60, unit: 'minute' },
  { limit: 86_400, divisor: 3600, unit: 'hour' },
  { limit: 604_800, divisor: 86_400, unit: 'day' },
  { limit: 2_629_800, divisor: 604_800, unit: 'week' },
  { limit: 31_557_600, divisor: 2_629_800, unit: 'month' },
];

/** "3 minutes ago", "in 2 days" — in the locale's own words. */
export function formatRelative(
  value: Date | string | number,
  now: Date = new Date(),
  locale: string = getActiveLocale(),
): string {
  const seconds = Math.round((toDate(value).getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });
  for (const step of RELATIVE_STEPS) {
    if (abs < step.limit) return rtf.format(Math.round(seconds / step.divisor), step.unit);
  }
  return rtf.format(Math.round(seconds / 31_557_600), 'year');
}

/** "Dutch" for `nl`, in the active UI language; the code itself when the engine does not know it. */
export function formatLanguageName(code: string, locale: string = getActiveLocale()): string {
  const base = code.split('-')[0] ?? code;
  try {
    return new Intl.DisplayNames([locale], { type: 'language' }).of(base) ?? base;
  } catch {
    return base;
  }
}

/** A short duration from minutes: "12 min", "1 h 5 min". Negative values read as overdue by that much. */
export function formatMinutes(minutes: number, locale: string = getActiveLocale()): string {
  const abs = Math.abs(Math.round(minutes));
  const unit = (value: number, u: 'hour' | 'minute'): string =>
    new Intl.NumberFormat(locale, { style: 'unit', unit: u, unitDisplay: 'short' }).format(value);
  if (abs < 60) return unit(abs, 'minute');
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  return m === 0 ? unit(h, 'hour') : `${unit(h, 'hour')} ${unit(m, 'minute')}`;
}

/** "A, B and C" in the reader's language (names are not translated). */
export function formatList(items: readonly string[], locale: string = getActiveLocale()): string {
  try {
    return new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(items);
  } catch {
    return items.join(', ');
  }
}

/**
 * Old IANA names some browsers still report (ICU keeps the legacy link as the
 * "canonical" id): Chrome on an Indian machine says Asia/Calcutta. Each maps
 * to the current name, which is what the workspace stores and what a person
 * expects to read.
 */
const LEGACY_TIME_ZONES: Readonly<Record<string, string>> = {
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Dacca': 'Asia/Dhaka',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Asia/Thimbu': 'Asia/Thimphu',
  'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
  'Europe/Kiev': 'Europe/Kyiv',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Indianapolis': 'America/Indiana/Indianapolis',
  'Pacific/Truk': 'Pacific/Chuuk',
  'Pacific/Ponape': 'Pacific/Pohnpei',
};

/** The current IANA name of a time zone (Asia/Calcutta → Asia/Kolkata); unknown names pass through. */
export function canonicalTimeZone(timeZone: string): string {
  return LEGACY_TIME_ZONES[timeZone] ?? timeZone;
}

/** The reader's IANA time zone under its current name; UTC where the browser does not say. */
export function browserTimeZone(): string {
  try {
    return canonicalTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  } catch {
    return 'UTC';
  }
}
